import { motion } from "framer-motion";
import {
    Instagram,
    Youtube,
    Mail,
    MessageCircle,
    Music2,
    Linkedin,
    Phone,
    Facebook,
    ArrowUpRight,
} from "lucide-react";
import { socials, socialHandles } from "../../lib/socials";
import { useI18n } from "../../lib/i18n";

export default function Contact() {
    const { t } = useI18n();
    const s = t.contact;
    const l = s.labels;

    const channels = [
        {
            label: s.channels.WhatsApp,
            handle: socialHandles.whatsapp,
            href: socials.whatsapp,
            Icon: MessageCircle,
            testid: "contact-whatsapp",
            featured: true,
        },
        {
            label: s.channels.Instagram,
            handle: socialHandles.instagram,
            href: socials.instagram,
            Icon: Instagram,
            testid: "contact-instagram",
        },
        {
            label: s.channels.YouTube,
            handle: socialHandles.youtube,
            href: socials.youtube,
            Icon: Youtube,
            testid: "contact-youtube",
        },
        {
            label: s.channels.Email,
            handle: socialHandles.email,
            href: `mailto:${socials.email}`,
            Icon: Mail,
            testid: "contact-email",
        },
        {
            label: s.channels.TikTok,
            handle: socialHandles.tiktok,
            href: socials.tiktok,
            Icon: Music2,
            testid: "contact-tiktok",
        },
        {
            label: s.channels.Facebook,
            handle: socialHandles.facebook,
            href: socials.facebook,
            Icon: Facebook,
            testid: "contact-facebook",
        },
        {
            label: s.channels.LinkedIn,
            handle: socialHandles.linkedin,
            href: socials.linkedin,
            Icon: Linkedin,
            testid: "contact-linkedin",
        },
        {
            label: s.channels.Viber,
            handle: socialHandles.viber,
            href: socials.viber,
            Icon: Phone,
            testid: "contact-viber",
        },
    ];

    return (
        <section
            id="contact"
            className="section relative overflow-hidden"
            data-testid="contact-section"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full"
                style={{
                    background:
                        "radial-gradient(circle at center, rgba(229,91,19,0.22) 0%, rgba(229,91,19,0.04) 30%, transparent 60%)",
                    filter: "blur(30px)",
                }}
            />

            <div className="container-x relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-8"
                >
                    <span className="w-8 h-px bg-[#E55B13]" />
                    {s.eyebrow}
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                    className="mega-text text-[16vw] md:text-[12vw] lg:text-[10vw] text-white leading-[0.92]"
                    data-testid="contact-headline"
                >
                    {s.titlePre}
                    <span className="italic font-extralight text-white/60">
                        {s.titleAccent}
                    </span>
                    <span className="accent-dot" />
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                        duration: 1,
                        delay: 0.15,
                        ease: [0.16, 1, 0.3, 1],
                    }}
                    className="mt-8 md:mt-10 font-body text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl"
                >
                    {s.subtitle.pre}
                    <span className="text-white">{s.subtitle.a}</span>
                    {s.subtitle.sep1}
                    <span className="text-white">{s.subtitle.b}</span>
                    {s.subtitle.sep2}
                    <span className="text-white">{s.subtitle.c}</span>
                    {s.subtitle.sep3}
                    <span className="text-white">{s.subtitle.d}</span>
                    {s.subtitle.and}
                    <span className="text-white">{s.subtitle.e}</span>
                    {s.subtitle.post}
                </motion.p>

                <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                            duration: 1,
                            delay: 0.05,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                        className="lg:col-span-4 space-y-8"
                    >
                        <div>
                            <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/40 mb-3">
                                {l.direct}
                            </div>
                            <a
                                href={`mailto:${socials.email}`}
                                className="font-heading text-2xl md:text-3xl font-light text-white hover:text-[#E55B13] transition-colors tracking-[-0.02em] break-all"
                                data-testid="contact-direct-email"
                            >
                                {socials.email}
                            </a>
                        </div>
                        <div>
                            <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/40 mb-3">
                                {l.based}
                            </div>
                            <div className="font-heading text-xl font-light text-white/85">
                                {l.location}
                            </div>
                            <div className="mt-1 font-mono text-[11px] tracking-[0.22em] uppercase text-white/40">
                                {l.worldwide}
                            </div>
                        </div>
                        <div>
                            <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/40 mb-3">
                                {l.response}
                            </div>
                            <div className="font-heading text-xl font-light text-white/85">
                                {l.within}
                            </div>
                        </div>
                    </motion.div>

                    <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {channels.map((c, i) => (
                            <motion.a
                                key={c.testid}
                                href={c.href}
                                target={
                                    c.href.startsWith("mailto") ||
                                    c.href.startsWith("viber")
                                        ? undefined
                                        : "_blank"
                                }
                                rel="noopener noreferrer"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.8,
                                    delay: 0.1 + i * 0.05,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className={`group flex items-center justify-between p-6 md:p-7 rounded-3xl border transition-all duration-500 card-lift ${
                                    c.featured
                                        ? "bg-gradient-to-br from-[#E55B13]/[0.14] to-transparent border-[#E55B13]/40 hover:border-[#E55B13]"
                                        : "bg-white/[0.03] border-white/[0.08] hover:border-[#E55B13]/50 hover:bg-white/[0.06]"
                                }`}
                                data-testid={c.testid}
                            >
                                <div className="flex items-center gap-5">
                                    <div
                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors duration-500 ${
                                            c.featured
                                                ? "bg-[#E55B13] text-black"
                                                : "bg-white/[0.05] border border-white/10 text-white group-hover:text-[#E55B13] group-hover:border-[#E55B13]/40"
                                        }`}
                                    >
                                        <c.Icon size={18} strokeWidth={1.6} />
                                    </div>
                                    <div>
                                        <div className="font-heading text-lg md:text-xl font-medium tracking-[-0.01em] text-white">
                                            {c.label}
                                        </div>
                                        <div className="font-mono text-[11px] tracking-[0.16em] text-white/50 mt-1 truncate max-w-[220px]">
                                            {c.handle}
                                        </div>
                                    </div>
                                </div>
                                <ArrowUpRight
                                    size={18}
                                    className="text-white/40 group-hover:text-[#E55B13] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                                />
                            </motion.a>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
